import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentApi } from '../../services/api';
import LessonIcon, { levelFromGrade } from '../../components/LessonIcon';
import { useAuth } from '../../contexts/AuthContext';
import { goalAuthorBadge, isSelfSetGoal } from '../../utils/goalAuthor';

const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

// P-2: week navigator — Monday-anchored. Adding/subtracting offsetWeeks
// shifts the rendered week without touching any server-side state.
function weekStartForOffset(offsetWeeks: number): Date {
  const d = new Date();
  const jsDow = d.getDay(); // 0=Sun..6=Sat
  const diff = (jsDow + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const fmt = (x: Date) => `${x.getDate()} ${x.toLocaleDateString('tr-TR', { month: 'short' })}`;
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

// Phase 8 — completion (the green check) is now teacher-write, student-read.
// What used to be student-side localStorage attendance is gone; we render the
// is_completed flag the backend returns on each schedule entry. The toggle
// button stays on the page but is non-clickable for students; the teacher
// panel exposes the same state via its own toggle endpoint.

interface ScheduleEntry {
  id: number | string;
  day_of_week: number; // 0..6 (Pazartesi=0 … Pazar=6 per DB check constraint)
  start_time?: string | null;
  end_time?: string | null;
  // DB column is `title`; older code referred to it as `topic`. Accept both.
  title?: string | null;
  topic?: string | null;
  subject_id?: number | null;
  question_target?: number | null;
  target_questions?: number | null;
  duration_minutes?: number | null;
  subjects?: { id?: number; name?: string };
  // Phase 7: only set when a teacher-assigned homework backs this slot.
  // Both null = self-set entry; Programım card hides Ödeve Git / Kitaba Git
  // because the buttons would have nowhere to go.
  book_id?: number | null;
  assignment_id?: string | null;
  // Phase 8: read-only completion flag, set by the teacher.
  is_completed?: boolean;
  completed_at?: string | null;
}

interface ScheduleGroup {
  id: number | string;
  name?: string | null;
  study_schedule_entries: ScheduleEntry[];
}

// T9 — one manual "dışarıda çözdüm" log row (goal_practice_entries).
interface PracticeEntry {
  id: number | string;
  entry_date: string;
  solved_questions: number;
  correct_count: number;
  wrong_count: number;
}

interface Goal {
  id: number | string | bigint;
  target_value?: number;
  achieved_value?: number;
  correct_count?: number;
  wrong_count?: number;
  accuracy_rate?: number | string;
  start_time?: string | null;
  end_time?: string | null;
  goal_date?: string | null;
  status?: string;
  // Student-set period goals (auto-tracked from solved questions).
  period_type?: 'daily' | 'weekly' | 'monthly' | string;
  period_start?: string | null;
  period_end?: string | null;
  auto_tracked?: boolean;
  is_archived?: boolean;
  subject_name?: string | null;
  created_by_type?: string;
  // T9 — manual off-platform sums (already included in achieved_value).
  manual_solved?: number;
  manual_correct?: number;
  manual_wrong?: number;
  goal_practice_entries?: PracticeEntry[];
  goal_definitions?: { title?: string; subjects?: { name?: string } | null };
  subject_units?: { name?: string; subjects?: { name?: string } };
}

function fmtTimeRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return '—';
  const f = (s?: string | null) => {
    if (!s) return '—';
    const d = new Date(s);
    return d.getUTCHours().toString().padStart(2, '0') + ':' + d.getUTCMinutes().toString().padStart(2, '0');
  };
  return `${f(start)} - ${f(end)}`;
}

function computeDuration(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  const mins = Math.round((e.getTime() - s.getTime()) / 60000);
  return mins > 0 ? mins : null;
}

type View = 'program' | 'hedef';
type Modal = null | 'veri-gir' | 'dis-veri' | 'kaydedildi' | 'silindi' | 'program-edit' | 'confirm-delete' | 'yeni-hedef';

const PERIOD_LABEL: Record<string, string> = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };

function localTodayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// T8 — mirrors backend computeGoalPeriod (UTC math): daily → the day itself,
// weekly → Monday–Sunday of the week containing the date, monthly → whole
// month. Used to preview the resolved window under the date picker.
function resolveGoalPeriod(periodType: 'daily' | 'weekly' | 'monthly', dateIso: string): { start: Date; end: Date } | null {
  const ref = new Date(`${dateIso}T00:00:00Z`);
  if (isNaN(ref.getTime())) return null;
  const y = ref.getUTCFullYear(), m = ref.getUTCMonth(), d = ref.getUTCDate();
  if (periodType === 'weekly') {
    const diff = (ref.getUTCDay() + 6) % 7; // days since Monday
    return { start: new Date(Date.UTC(y, m, d - diff)), end: new Date(Date.UTC(y, m, d - diff + 6)) };
  }
  if (periodType === 'monthly') {
    return { start: new Date(Date.UTC(y, m, 1)), end: new Date(Date.UTC(y, m + 1, 0)) };
  }
  return { start: ref, end: ref };
}

function fmtUtcShortDate(d: Date): string {
  return `${d.getUTCDate()} ${d.toLocaleDateString('tr-TR', { month: 'short', timeZone: 'UTC' })}`;
}

// "15 Tem" style short date; for weekly/monthly shows the start–end range.
function goalPeriodRange(g: { period_type?: string; period_start?: string | null; period_end?: string | null; goal_date?: string | null }): string {
  const fmt = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getUTCDate()} ${d.toLocaleDateString('tr-TR', { month: 'short', timeZone: 'UTC' })}`;
  };
  const start = g.period_start ?? g.goal_date;
  const end = g.period_end ?? g.goal_date;
  if (!start) return '';
  if (!end || start === end || g.period_type === 'daily') return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}
type ConfirmTarget = { kind: 'entry'; id: string | number } | { kind: 'goal'; id: string | number };

// Solid flame icon (no emoji) for the weekly-win celebration (B17).
const FlameIcon: React.FC<{ size?: number; color?: string }> = ({ size = 28, color = '#f97316' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

const StudentSchedule: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const studentLevel = levelFromGrade((user as any)?.grade?.grade_number ?? null);
  const [view, setView] = useState<View>('program');
  const [modal, setModal] = useState<Modal>(null);
  const [schedules, setSchedules] = useState<ScheduleGroup[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  // Hedef Görünümü: aktif vs arşiv, plus the "Yeni Hedef" form.
  const [hedefSubTab, setHedefSubTab] = useState<'aktif' | 'arsiv'>('aktif');
  // T8 — `date` anchors the goal's period: the picked day (günlük), any day
  // of the wanted week (haftalık) or month (aylık). Backend snaps the window.
  const [goalForm, setGoalForm] = useState<{ periodType: 'daily' | 'weekly' | 'monthly'; subjectId: string; targetCount: string; date: string }>({
    periodType: 'daily', subjectId: '', targetCount: '30', date: localTodayIso(),
  });
  // T9 — "Dışarıda Çözdüklerim" manual-entry form (per auto-tracked goal).
  const [disForm, setDisForm] = useState<{ date: string; solved: string; dogru: string; yanlis: string }>({
    date: localTodayIso(), solved: '10', dogru: '0', yanlis: '0',
  });

  // P-2: week navigation. 0 = this week, -1 = last week, +1 = next week, etc.
  // Past, current, and future are all reachable — the UI only disables the
  // left/right arrows if a product decision adds a hard floor/ceiling.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(() => weekStartForOffset(weekOffset), [weekOffset]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);
  const weekRangeLabel = formatWeekRange(weekStart);

  // Completion state is now read-only on the student side — derived directly
  // from each entry's is_completed flag. Teacher's toggle endpoints write it.
  // The local Set keeps the same shape so the existing render code stays
  // untouched, but it's purely a derived view of API data.
  const attended: Record<string, Set<string>> = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    const allEntries = schedules.flatMap((s) => s.study_schedule_entries ?? []);
    for (const d of weekDays) {
      // Use local date string to match todayIso (which is also local).
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const dow = (d.getDay() + 6) % 7;
      const ids = new Set<string>();
      for (const e of allEntries) {
        if (e.day_of_week === dow && e.is_completed) ids.add(String(e.id));
      }
      out[iso] = ids;
    }
    return out;
  }, [schedules, weekDays]);
  const [formData, setFormData] = useState({
    subjectUnitId: '', targetCount: '50', goalDate: new Date().toISOString().slice(0, 10),
    startTime: '10:00', endTime: '12:00', goalTitle: 'Günlük Soru Çözümü',
    toplamSoru: '50', cozulenSoru: '0', dogru: '0', yanlis: '0',
  });

  // Subjects for the dropdown come from the DB, scoped to this student's
  // actual curriculum. No hardcoded subject list — see /student/subjects.
  const [SUBJECT_OPTIONS, setSubjectOptions] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    studentApi.subjects()
      .then((res) => setSubjectOptions((res.data ?? []).map((s: any) => ({ id: String(s.id), name: s.name }))))
      .catch(() => setSubjectOptions([]));
  }, []);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([studentApi.schedule(), studentApi.goals()])
      .then(([s, g]) => {
        setSchedules(s.data || []);
        setGoals(g.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const allEntries: ScheduleEntry[] = schedules.flatMap((s) => s.study_schedule_entries ?? []);
  // DB constraint pins day_of_week to 0..6 (Pazartesi=0 … Pazar=6).
  const entriesByDay: Record<number, (ScheduleEntry & { __isGoal?: boolean; __goalId?: string })[]> = {};
  for (let i = 0; i < 7; i++) entriesByDay[i] = [];
  for (const e of allEntries) {
    if (e.day_of_week >= 0 && e.day_of_week <= 6) entriesByDay[e.day_of_week].push(e);
  }
  // Surface hedefler on the weekly grid — students usually keep a plan for
  // a month or more, so we don't filter by "this week only". Every goal
  // shows on its matching day-of-week column, regardless of which calendar
  // week the goal_date falls in. Duplicates on the same day-of-week are
  // de-duplicated by time+title so the grid stays clean.
  //
  // T10 — untimed goals used to be skipped entirely, which is why a goal
  // created in Hedef Görünümü showed up for the teacher but never on the
  // student's own program. Single-day goals (günlük) now render as a card on
  // their date's column; weekly/monthly goals get the progress band under
  // the grid instead (T11).
  const goalDedup = new Set<string>();
  for (const g of goals) {
    if (!g.goal_date) continue;
    const hasTimes = !!g.start_time && !!g.end_time;
    const isMultiDay = g.period_type === 'weekly' || g.period_type === 'monthly';
    if (!hasTimes && isMultiDay) continue; // rendered in the band below the grid
    const gd = new Date(g.goal_date);
    // day-of-week index (0=Mon..6=Sun) independent of which week the date lands on.
    const jsDow = gd.getUTCDay(); // 0=Sun..6=Sat
    const dowIdx = (jsDow + 6) % 7;
    const title = g.goal_definitions?.subjects?.name || g.goal_definitions?.title || 'Hedef';
    const startStr = typeof g.start_time === 'string' ? g.start_time.slice(11, 16) : '';
    // Untimed goals keep their id in the key — two subject-less daily goals
    // on the same weekday must not collapse into one card.
    const dedupKey = hasTimes ? `${dowIdx}|${startStr}|${title}` : `${dowIdx}|id:${g.id}`;
    if (goalDedup.has(dedupKey)) continue;
    goalDedup.add(dedupKey);
    entriesByDay[dowIdx].push({
      id: `goal-${g.id}`,
      day_of_week: dowIdx,
      start_time: typeof g.start_time === 'string' ? g.start_time : null,
      end_time: typeof g.end_time === 'string' ? g.end_time : null,
      title,
      question_target: g.target_value,
      subject_id: (g.goal_definitions?.subjects as any)?.id ?? null,
      subjects: g.goal_definitions?.subjects ? { id: (g.goal_definitions.subjects as any).id, name: g.goal_definitions.subjects.name } : undefined,
      __isGoal: true,
      __goalId: String(g.id),
    } as any);
  }
  // Sort each day by start time so goals and schedule entries interleave.
  for (const k of Object.keys(entriesByDay)) {
    const arr = entriesByDay[Number(k)];
    arr.sort((a, b) => {
      const at = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bt = b.start_time ? new Date(b.start_time).getTime() : 0;
      return at - bt;
    });
  }

  const askDeleteEntry = (entryId: string | number) => {
    setConfirmTarget({ kind: 'entry', id: entryId });
    setModal('confirm-delete');
  };
  const askDeleteGoal = (goalId: string | number | bigint) => {
    // Students can't delete goals assigned from above — teacher, koç or a
    // kurum toplu-hedef upload (button is also hidden below).
    const g = goals.find((x) => String(x.id) === String(goalId));
    if (!isSelfSetGoal(g?.created_by_type)) return;
    setConfirmTarget({ kind: 'goal', id: String(goalId) });
    setModal('confirm-delete');
  };

  const executeConfirmedDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.kind === 'entry') {
        await studentApi.deleteScheduleEntry(String(confirmTarget.id));
      } else {
        await studentApi.deleteGoal(String(confirmTarget.id));
      }
      await loadAll();
      setModal(null);
      setConfirmTarget(null);
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? e?.message ?? 'Silinemedi');
    }
  };

  const handleCreateGoal = async () => {
    setFormError(null);
    const target = parseInt(goalForm.targetCount, 10);
    if (!target || target <= 0) { setFormError('Geçerli bir hedef soru sayısı girin.'); return; }
    if (!goalForm.date || !resolveGoalPeriod(goalForm.periodType, goalForm.date)) {
      setFormError('Geçerli bir tarih seçin.');
      return;
    }
    try {
      await studentApi.createGoal({
        periodType: goalForm.periodType,
        subjectId: goalForm.subjectId || undefined,
        targetCount: target,
        date: goalForm.date,
      });
      await loadAll();
      setModal(null);
      setGoalForm({ periodType: 'daily', subjectId: '', targetCount: '30', date: localTodayIso() });
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? 'Hedef oluşturulamadı');
    }
  };

  // T9 — log questions solved OUTSIDE Digipo onto an auto-tracked goal.
  const handleAddPractice = async () => {
    if (!editingGoal) return;
    setFormError(null);
    const solved = parseInt(disForm.solved, 10);
    const dogru = parseInt(disForm.dogru, 10) || 0;
    const yanlis = parseInt(disForm.yanlis, 10) || 0;
    if (!solved || solved <= 0) { setFormError('Geçerli bir soru sayısı girin.'); return; }
    if (dogru < 0 || yanlis < 0) { setFormError('Doğru/yanlış sayıları negatif olamaz.'); return; }
    if (dogru + yanlis > solved) { setFormError('Doğru + yanlış toplamı çözülen sorudan büyük olamaz.'); return; }
    try {
      await studentApi.addGoalPractice(String(editingGoal.id), {
        date: disForm.date || undefined,
        solved,
        correct: dogru,
        wrong: yanlis,
      });
      await loadAll();
      setModal('kaydedildi');
      setTimeout(() => setModal(null), 1200);
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? e?.message ?? 'Kaydedilemedi');
    }
  };

  const handleDeletePractice = async (goalId: string, entryId: string) => {
    setFormError(null);
    try {
      await studentApi.deleteGoalPractice(goalId, entryId);
      await loadAll();
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? e?.message ?? 'Silinemedi');
    }
  };

  const handleSaveEntryEdit = async () => {
    if (!editingEntry) return;
    setFormError(null);
    try {
      await studentApi.updateScheduleEntry(String(editingEntry.id), {
        title: formData.goalTitle || editingEntry.topic || undefined,
        subjectId: formData.subjectUnitId ? parseInt(formData.subjectUnitId, 10) : undefined,
        startTime: formData.startTime,
        endTime: formData.endTime,
        targetQuestions: parseInt(formData.targetCount, 10) || undefined,
      });
      await loadAll();
      setModal(null);
      setEditingEntry(null);
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? 'Güncellenemedi');
    }
  };

  const handleUpdateGoal = async () => {
    if (!editingGoal) return;
    const newValue = parseInt(formData.cozulenSoru, 10) || 0;
    const correct = parseInt(formData.dogru, 10) || 0;
    const wrong = parseInt(formData.yanlis, 10) || 0;
    setFormError(null);
    if (correct + wrong > newValue) {
      setFormError('Doğru + yanlış toplamı çözülen sorudan büyük olamaz.');
      return;
    }
    // Optimistically reflect the new values on the card so the user sees
    // the progress bar tick up immediately, even if the refetch hiccups.
    setGoals((prev) => prev.map((g) => (
      String(g.id) === String(editingGoal.id)
        ? { ...g, achieved_value: newValue, correct_count: correct, wrong_count: wrong }
        : g
    )));
    try {
      await studentApi.updateGoal(String(editingGoal.id), newValue, correct, wrong);
      await loadAll();
      setModal('kaydedildi');
      setTimeout(() => setModal(null), 1200);
    } catch (e: any) {
      console.error('updateGoal failed', e);
      // Roll back the optimistic update by reloading from the server.
      try { await loadAll(); } catch { /* ignore chained failure */ }
      // Keep the modal open and show the error inline so the user knows
      // what happened instead of seeing a silent close.
      const msg = e?.response?.data?.error ?? e?.message ?? 'Güncellenemedi';
      setFormError(msg === 'Network Error' ? 'Sunucuya bağlanılamadı. Lütfen tekrar deneyin.' : msg);
    }
  };

  // P-6: progress bars — weekly and today's. Derived from:
  //   - goals whose target_value is met (achieved_value >= target_value)
  //   - schedule entries marked complete by the teacher (is_completed flag)
  // Use local date for "today" so the bar doesn't shift at midnight UTC while
  // the student is in Turkey (UTC+3).
  const todayLocal = new Date();
  const todayIso = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth() + 1).padStart(2, '0')}-${String(todayLocal.getDate()).padStart(2, '0')}`;
  const toLocalIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const isTodayInWeek = weekDays.some(d => toLocalIso(d) === todayIso);

  const weekProgress = useMemo(() => {
    let total = 0;
    let done = 0;
    // B16 — question-based progress across the week's goals (same rules as the
    // daily bar): overshoot capped per goal so the week can't exceed 100%.
    let qTarget = 0;
    let qDone = 0;
    // Goals that fall in the selected week
    for (const g of goals) {
      if (!g.goal_date) continue;
      const iso = String(g.goal_date).slice(0, 10);
      if (!weekDays.some(d => toLocalIso(d) === iso)) continue;
      total++;
      const achieved = g.achieved_value ?? 0;
      const target = g.target_value ?? 0;
      if (target > 0 && achieved >= target) done++;
      if (target > 0) {
        qTarget += target;
        qDone += Math.min(achieved, target);
      }
    }
    // Schedule entries repeat weekly by day-of-week — counted once per day.
    for (const d of weekDays) {
      const iso = toLocalIso(d);
      const dow = ((d.getDay() + 6) % 7); // Mon=0..Sun=6
      for (const e of allEntries) {
        if (e.day_of_week !== dow) continue;
        total++;
        if (attended[iso]?.has(String(e.id))) done++;
      }
    }
    return {
      total,
      done,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
      qTarget,
      qDone,
      qPct: qTarget > 0 ? Math.round((qDone / qTarget) * 100) : 0,
    };
  }, [goals, weekDays, allEntries, attended]);

  // B17 — celebrate when this week's progress hits 100%. Fires once per week
  // (localStorage keyed by the Monday) and only for the current week, so
  // browsing an old completed week or re-rendering won't re-pop the modal.
  const [showWeekWin, setShowWeekWin] = useState(false);
  useEffect(() => {
    if (weekOffset !== 0) return;
    if (!(weekProgress.total > 0 && weekProgress.pct >= 100)) return;
    const key = `dijipo_week_win_${toLocalIso(weekDays[0])}`;
    try {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        setShowWeekWin(true);
      }
    } catch { /* storage blocked — skip celebration rather than nag every render */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, weekProgress.total, weekProgress.pct]);

  const dailyProgress = useMemo(() => {
    const iso = todayIso;
    if (!isTodayInWeek) return { total: 0, done: 0, pct: 0, qTarget: 0, qDone: 0, qPct: 0 };
    let total = 0;
    let done = 0;
    // B16 — question-based progress: sum solved vs target questions across
    // today's goals so the bar advances partially (eski yapı = tamamlanan
    // hedef sayısı; yeni = çözülen soru yüzdesi). Overshoot is capped per goal
    // so one over-achieved goal can't push the day past 100%.
    let qTarget = 0;
    let qDone = 0;
    for (const g of goals) {
      if (!g.goal_date) continue;
      // goal_date from DB is "YYYY-MM-DDTHH:mm:ss.000Z" — take the UTC date
      // part which matches the local date the teacher/student chose (stored as
      // DATE, not DATETIME, so the time component is always midnight UTC).
      const goalDateIso = String(g.goal_date).slice(0, 10);
      if (goalDateIso !== iso) continue;
      total++;
      const achieved = g.achieved_value ?? 0;
      const target = g.target_value ?? 0;
      if (target > 0 && achieved >= target) done++;
      if (target > 0) {
        qTarget += target;
        qDone += Math.min(achieved, target);
      }
    }
    // Derive day-of-week from todayIso (local date) at noon so DST can't shift it.
    const dow = ((new Date(`${todayIso}T12:00:00`).getDay() + 6) % 7);
    for (const e of allEntries) {
      if (e.day_of_week !== dow) continue;
      total++;
      if (attended[iso]?.has(String(e.id))) done++;
    }
    return {
      total,
      done,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
      qTarget,
      qDone,
      qPct: qTarget > 0 ? Math.round((qDone / qTarget) * 100) : 0,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, allEntries, attended, todayIso, isTodayInWeek]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-[18px] xl:text-[24px] font-semibold text-[#2b2b2b]">Çalışma Planım</h1>
        <button
          onClick={() => setView('program')}
          className={`px-4 xl:px-5 py-2 xl:py-2.5 rounded-[12px] text-[12px] xl:text-[16px] font-bold transition-colors ${view === 'program' ? 'bg-[#e5599b] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}
        >
          Program Görünümü
        </button>
        <button
          onClick={() => setView('hedef')}
          className={`px-4 xl:px-5 py-2 xl:py-2.5 rounded-[12px] text-[12px] xl:text-[16px] font-bold transition-colors ${view === 'hedef' ? 'bg-[#e5599b] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}
        >
          Hedef Görünümü
        </button>
      </div>

      {/* P-2: week selector. Past/current/future are all reachable. */}
      {view === 'program' && (
        <div className="bg-[#fafafa] rounded-[16px] p-3 xl:p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setWeekOffset(o => o - 1)}
            aria-label="Önceki hafta"
            className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <div className="text-center flex-1 min-w-[180px]">
            <p className="text-[12px] text-gray-500">
              {weekOffset === 0 ? 'Bu Hafta' : weekOffset < 0 ? `${Math.abs(weekOffset)} hafta önce` : `${weekOffset} hafta sonra`}
            </p>
            <p className="text-[15px] xl:text-[17px] font-bold text-[#2b2b2b]">{weekRangeLabel}</p>
          </div>
          <button
            onClick={() => setWeekOffset(o => o + 1)}
            aria-label="Sonraki hafta"
            className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              className="bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] hover:bg-blue-700"
            >
              Bu Haftaya Dön
            </button>
          )}
        </div>
      )}

      {/* P-6: progress bars for the selected week (always) plus today's if
          today falls in the visible week. Weekly progress keeps going after
          a day's 100%. */}
      {view === 'program' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="bg-[#fafafa] rounded-[16px] p-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#3973bf]">Haftalık İlerleme</span>
              <span className="text-[14px] font-bold text-[#3973bf]">%{weekProgress.pct}</span>
            </div>
            <div className="w-full h-[10px] bg-[#e5e5e5] rounded-full overflow-hidden">
              <div className="h-full bg-[#3973bf]" style={{ width: `${weekProgress.pct}%` }} />
            </div>
            <p className="text-[12px] text-gray-500 mt-1">{weekProgress.done} / {weekProgress.total} tamamlandı</p>

            {/* B16 — haftalık çözülen soru yüzdesi (tamamlama yapısına ek). */}
            {weekProgress.qTarget > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] font-semibold text-[#3973bf]">Çözülen Soru</span>
                  <span className="text-[12px] font-bold text-[#3973bf]">%{weekProgress.qPct}</span>
                </div>
                <div className="w-full h-[8px] bg-[#e5e5e5] rounded-full overflow-hidden">
                  <div className="h-full bg-[#3973bf] opacity-70" style={{ width: `${weekProgress.qPct}%` }} />
                </div>
                <p className="text-[12px] text-gray-500 mt-1">{weekProgress.qDone} / {weekProgress.qTarget} soru çözüldü</p>
              </div>
            )}
          </div>
          <div className="bg-[#fafafa] rounded-[16px] p-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#e5599b]">Günlük İlerleme</span>
              <span className="text-[14px] font-bold text-[#e5599b]">%{dailyProgress.pct}</span>
            </div>
            <div className="w-full h-[10px] bg-[#e5e5e5] rounded-full overflow-hidden">
              <div className="h-full bg-[#e5599b]" style={{ width: `${dailyProgress.pct}%` }} />
            </div>
            <p className="text-[12px] text-gray-500 mt-1">
              {isTodayInWeek ? `${dailyProgress.done} / ${dailyProgress.total} tamamlandı` : 'Bugün bu haftada değil'}
            </p>

            {/* B16 — çözülen soru sayısına göre yüzdelik ilerleme (eski
                tamamlama yapısına ek). Only when today has question goals. */}
            {isTodayInWeek && dailyProgress.qTarget > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] font-semibold text-[#e5599b]">Çözülen Soru</span>
                  <span className="text-[12px] font-bold text-[#e5599b]">%{dailyProgress.qPct}</span>
                </div>
                <div className="w-full h-[8px] bg-[#e5e5e5] rounded-full overflow-hidden">
                  <div className="h-full bg-[#e5599b] opacity-70" style={{ width: `${dailyProgress.qPct}%` }} />
                </div>
                <p className="text-[12px] text-gray-500 mt-1">{dailyProgress.qDone} / {dailyProgress.qTarget} soru çözüldü</p>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : view === 'program' ? (
        <>
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-7 gap-2 mb-2">
              {DAY_NAMES.map((day, i) => {
                const d = weekDays[i];
                const isoDate = toLocalIso(d);
                const isToday = isoDate === todayIso;
                return (
                  <div key={i} className="text-center">
                    <div className={`rounded-[12px] py-2 xl:py-2.5 ${isToday ? 'bg-[#3973bf]' : 'bg-[#e5599b]'}`}>
                      <span className="block text-[11px] xl:text-[14px] font-semibold text-white">{day}</span>
                      <span className="block text-[10px] xl:text-[12px] text-white/90">{d.getDate()} {d.toLocaleDateString('tr-TR', { month: 'short' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {DAY_NAMES.map((_, dayIdx) => {
                const entries = entriesByDay[dayIdx] ?? [];
                const d = weekDays[dayIdx];
                const isoDate = toLocalIso(d);
                // For this specific date, keep only the goals whose goal_date
                // matches — entries with day_of_week always show, goals only
                // when they fall inside the selected week.
                const dayEntries = entries.filter(e => {
                  if (!(e as any).__isGoal) return true;
                  const goal = goals.find(g => String(g.id) === String((e as any).__goalId));
                  if (!goal?.goal_date) return true;
                  return String(goal.goal_date).slice(0, 10) === isoDate;
                });
                return (
                  <div key={dayIdx} className="space-y-2 min-h-[400px]">
                    {dayEntries.length === 0 ? (
                      <div className="bg-[#fafafa] rounded-[12px] p-3 text-center text-[12px] text-gray-400 border border-gray-100">Boş</div>
                    ) : dayEntries.map((slot, si) => {
                      const color = si % 2 === 0 ? '#3973bf' : '#e5599b';
                      // T10 — goal cards carry live progress + completion from
                      // the goal row itself; schedule entries keep the
                      // teacher-set is_completed flag (attended set).
                      const goalMeta = (slot as any).__isGoal
                        ? goals.find(g => String(g.id) === String((slot as any).__goalId))
                        : undefined;
                      const isAttended = goalMeta
                        ? goalMeta.status === 'completed'
                        : (attended[isoDate]?.has(String(slot.id)) ?? false);
                      // Parent + teacher panels both use #dff5e0 + #11D600 for
                      // completed cards (Phase 11/15). Student matches now too
                      // so the family sees the same visual language across all
                      // three panels.
                      const textColor = color;
                      return (
                        <div
                          key={slot.id}
                          onClick={goalMeta ? () => setView('hedef') : undefined}
                          title={goalMeta ? 'Hedef Görünümünde aç' : undefined}
                          className={`rounded-[16px] relative overflow-hidden flex flex-col transition-colors duration-200 ${goalMeta ? 'cursor-pointer' : ''}`}
                          style={{
                            backgroundColor: isAttended ? '#dff5e0' : '#fafafa',
                            border: isAttended ? '2px solid #11D600' : '1px solid transparent',
                            minHeight: '140px',
                            boxShadow: '0px 1px 4px rgba(0,0,0,0.08)',
                          }}
                        >
                          {/* Faded subject icon as card background — Figma
                              spec: "ders logoları arkada silik şekilde
                              gözükmeli" inside the program view cards.
                              Positioned bottom-right at low opacity so the
                              text content stays readable. */}
                          {slot.subjects?.name && (
                            <div className="absolute right-1 bottom-1 pointer-events-none" style={{ opacity: 0.18 }}>
                              <LessonIcon
                                subject={slot.subjects.name}
                                level={studentLevel}
                                size={110}
                                fallback={<></>}
                              />
                            </div>
                          )}
                          {/* Phase 17 — student card is fully read-only now,
                              matching the parent panel exactly. The teacher
                              owns edits + deletes; the student sees their
                              program, marks attendance via teacher action,
                              and uses Ödeve Git / Kitaba Git to drill in.
                              The "+" button at the bottom of each day is
                              still available for student-initiated goals. */}
                          <div
                            className="relative p-2.5 xl:p-3 flex-1 flex flex-col"
                            style={{ paddingRight: 32 }}
                          >
                            {goalMeta && (
                              // AP70: öğretmen hedefi öğrencinin kendi hedefinden renkle de
                              // ayrışsın — öğretmen görünümündeki şemanın aynısı (kendi=turuncu,
                              // öğretmen=pembe). Eskiden ikisi de pembeydi.
                              <span className="inline-block self-start mb-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: goalAuthorBadge(goalMeta.created_by_type).bg, color: goalAuthorBadge(goalMeta.created_by_type).fg, fontFamily: 'Inter' }}>
                                {goalAuthorBadge(goalMeta.created_by_type).text}
                              </span>
                            )}
                            {(slot.title ?? slot.topic) && <p style={{ color: textColor, fontFamily: 'Inter', fontSize: 18, fontWeight: 700 }}>{(slot.title ?? slot.topic)}</p>}
                            {/* Goal pseudo-entries use the subject as their
                                title — don't print the same word twice. */}
                            {slot.subjects?.name && slot.subjects.name !== (slot.title ?? slot.topic) && <p style={{ color: textColor, fontFamily: 'Inter', fontSize: 15, fontWeight: 600 }}>{slot.subjects.name}</p>}
                            {(() => {
                              // Goal cards show progress on the bar below —
                              // a separate "N Soru" line would duplicate it.
                              if (goalMeta) return null;
                              const q = slot.question_target ?? slot.target_questions;
                              return q != null ? <p style={{ color: textColor, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{q} Soru</p> : null;
                            })()}
                            {(() => {
                              const dur = slot.duration_minutes ?? computeDuration(slot.start_time, slot.end_time);
                              if (dur == null) return null;
                              const hours = Math.round((dur / 60) * 10) / 10;
                              return <p style={{ color: textColor, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{hours} Saat</p>;
                            })()}
                            {/* T10 — live progress on goal cards, same source
                                as the Hedef Görünümü card (auto goals tick up
                                from solved questions). */}
                            {goalMeta && (() => {
                              const target = goalMeta.target_value ?? 0;
                              const achieved = goalMeta.achieved_value ?? 0;
                              const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                              const done = target > 0 && achieved >= target;
                              const barColor = done ? '#2fae4e' : textColor;
                              return (
                                <div className="mt-1">
                                  <div className="w-full h-[6px] bg-[#e0e0e0] rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                                  </div>
                                  <p style={{ color: barColor, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                                    {achieved} / {target} Soru{goalMeta.auto_tracked ? ' ⚡' : ''}
                                  </p>
                                </div>
                              );
                            })()}
                            {(slot.start_time || slot.end_time) && (
                              <p className="mt-auto" style={{ color: textColor, fontFamily: 'Inter', fontSize: 14, fontWeight: 700 }}>{fmtTimeRange(slot.start_time, slot.end_time)}</p>
                            )}
                          </div>
                          {/* Read-only completion indicator — same shape +
                              position as the parent panel (Phase 15). Only
                              renders when the teacher has marked it complete
                              so an unmarked card stays minimal. */}
                          {isAttended && (
                            <div
                              aria-label="Tamamlandı"
                              title={goalMeta ? 'Hedef tamamlandı' : 'Öğretmen tarafından tamamlandı olarak işaretlendi'}
                              className="absolute right-1.5 bottom-2.5 z-10 w-6 h-6 rounded-full flex items-center justify-center pointer-events-none"
                              style={{
                                background: '#11D600',
                                border: '2px solid #0DA600',
                                boxShadow: '0px 1px 2px rgba(0,0,0,0.18)',
                              }}
                            >
                              <svg width="11" height="9" viewBox="0 0 10 8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 4l3 3 5-6" />
                              </svg>
                            </div>
                          )}
                          {/* Action bar — only renders when the slot has at least one
                              real link (assignment_id for Ödeve Git, book_id for Kitaba
                              Git). Self-set schedule entries no longer sprout misleading
                              buttons that go nowhere. */}
                          {(slot.assignment_id || slot.book_id) && (
                            <div
                              className="flex gap-1 p-2 border-t"
                              style={{ borderColor: isAttended ? '#11D600' : '#f3f4f6', backgroundColor: '#ffffff' }}
                            >
                              {slot.assignment_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/student/odevler', {
                                      state: { openAssignmentId: slot.assignment_id, subjectFilter: slot.subjects?.name ?? '' },
                                    });
                                  }}
                                  className="flex-1 text-[11px] font-semibold rounded-[8px] py-1.5"
                                  style={{ color: isAttended ? '#0EA700' : '#3973bf', backgroundColor: '#ffffff', border: `1px solid ${isAttended ? '#0EA700' : '#3973bf'}` }}
                                >
                                  Ödeve Git
                                </button>
                              )}
                              {slot.book_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/student/kitap-icerik', { state: { bookId: slot.book_id } });
                                  }}
                                  className="flex-1 text-[11px] font-semibold rounded-[8px] py-1.5"
                                  style={{ color: isAttended ? '#0EA700' : '#e5599b', backgroundColor: '#ffffff', border: `1px solid ${isAttended ? '#0EA700' : '#e5599b'}` }}
                                >
                                  Kitaba Git
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* T11 — goals that span (part of) the visible week get their own
            progress strip under the grid. Weekly/monthly hedefler don't fit
            a single day column; this is where they live in Program
            Görünümü. Only renders when such a goal overlaps the week. */}
        {(() => {
          const weekStartIso = toLocalIso(weekDays[0]);
          const weekEndIso = toLocalIso(weekDays[6]);
          const spanning = goals.filter((g) => {
            if (g.period_type !== 'weekly' && g.period_type !== 'monthly') return false;
            const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
            const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
            if (!s || !e) return false;
            return s <= weekEndIso && e >= weekStartIso;
          });
          if (spanning.length === 0) return null;
          return (
            <div className="mt-4">
              <h2 className="text-[15px] xl:text-[18px] font-semibold text-[#2b2b2b] mb-2">Bu Haftayı Kapsayan Hedefler</h2>
              <div className="space-y-2">
                {spanning.map((g) => {
                  const target = g.target_value ?? 0;
                  const achieved = g.achieved_value ?? 0;
                  const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                  const done = target > 0 && achieved >= target;
                  const c = done ? '#2fae4e' : g.period_type === 'monthly' ? '#e5599b' : '#3973bf';
                  const subject = g.subject_name || g.goal_definitions?.subjects?.name || 'Genel';
                  return (
                    <div
                      key={String(g.id)}
                      onClick={() => setView('hedef')}
                      title="Hedef Görünümünde aç"
                      className="bg-[#fafafa] rounded-[16px] p-3 xl:p-4 cursor-pointer hover:shadow-md transition-shadow"
                      style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {/* AP70: bantta da hedefin sahibi görünsün (öğretmen görünümüyle aynı şema) */}
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: goalAuthorBadge(g.created_by_type).bg, color: goalAuthorBadge(g.created_by_type).fg }}>
                          {goalAuthorBadge(g.created_by_type).text}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c }}>
                          {PERIOD_LABEL[g.period_type ?? ''] ?? 'Hedef'}
                        </span>
                        <span className="text-[13px] xl:text-[15px] font-bold" style={{ color: c }}>{subject}</span>
                        <span className="text-[11px] xl:text-[12px] font-semibold text-gray-500">{goalPeriodRange(g)}</span>
                        {done && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2fae4e' }}>Tamamlandı</span>
                        )}
                        {g.auto_tracked && <span className="text-[11px] text-gray-400 ml-auto">⚡ Otomatik takip</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[8px] bg-[#e0e0e0] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: c }} />
                        </div>
                        <span className="text-[12px] xl:text-[14px] font-semibold whitespace-nowrap" style={{ color: c }}>
                          {achieved} / {target} Soru (%{pct})
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        </>
      ) : (
        <div>
          {/* Aktif / Arşiv sekmeleri + Yeni Hedef */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setHedefSubTab('aktif')}
              className={`px-4 py-2 rounded-[10px] text-[13px] xl:text-[15px] font-bold transition-colors ${hedefSubTab === 'aktif' ? 'bg-[#3973bf] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}
            >Aktif Hedefler</button>
            <button
              onClick={() => setHedefSubTab('arsiv')}
              className={`px-4 py-2 rounded-[10px] text-[13px] xl:text-[15px] font-bold transition-colors ${hedefSubTab === 'arsiv' ? 'bg-[#3973bf] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}
            >Arşiv</button>
            <button
              onClick={() => { setFormError(null); setGoalForm({ periodType: 'daily', subjectId: '', targetCount: '30', date: localTodayIso() }); setModal('yeni-hedef'); }}
              className="ml-auto bg-[#e5599b] text-white text-[13px] xl:text-[15px] font-bold px-5 py-2 rounded-[10px] hover:bg-pink-600 transition-colors flex items-center gap-1.5"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
              Yeni Hedef
            </button>
          </div>

          {(() => {
            const shown = goals
              .filter((g) => (hedefSubTab === 'arsiv' ? g.is_archived : !g.is_archived))
              .slice()
              .sort((a, b) => {
                const at = new Date(a.period_end ?? a.goal_date ?? 0).getTime();
                const bt = new Date(b.period_end ?? b.goal_date ?? 0).getTime();
                return hedefSubTab === 'arsiv' ? bt - at : at - bt;
              });
            if (shown.length === 0) {
              return (
                <p className="text-center text-gray-400 py-10">
                  {hedefSubTab === 'arsiv' ? 'Arşivde hedef yok.' : 'Aktif hedefin yok. "Yeni Hedef" ile bir tane oluştur.'}
                </p>
              );
            }
            return (
              <div className="space-y-3">
                {shown.map((g) => {
                  const target = g.target_value ?? 0;
                  const achieved = g.achieved_value ?? 0;
                  const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
                  const completed = target > 0 && achieved >= target;
                  const archived = !!g.is_archived;
                  // Accent: completed → green, aktif → blue, arşiv (tamamlanmamış) → gri.
                  const c = completed ? '#2fae4e' : archived ? '#888' : '#3973bf';
                  const subject = g.subject_name || g.goal_definitions?.subjects?.name || 'Genel';
                  const periodLabel = PERIOD_LABEL[g.period_type ?? 'daily'] ?? 'Hedef';
                  return (
                    <div key={String(g.id)} className="rounded-[24px] overflow-hidden relative" style={{ background: '#fafafa', boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
                      {/* Students may only delete their own goals — anything
                          assigned from above (teacher / koç / kurum) has no
                          delete button. */}
                      {isSelfSetGoal(g.created_by_type) && (
                        <button
                          onClick={() => askDeleteGoal(g.id)}
                          aria-label="Sil"
                          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/90 hover:bg-red-100 flex items-center justify-center text-[16px] text-[#2b2b2b] hover:text-red-600 leading-none"
                          style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.15)' }}
                        >×</button>
                      )}
                      <div className="flex">
                        <div className="relative overflow-hidden p-3 xl:p-4 flex-1" style={{ minHeight: '132px' }}>
                          <div className="flex items-center gap-2 flex-wrap mb-1 pr-8">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: goalAuthorBadge(g.created_by_type).bg, color: goalAuthorBadge(g.created_by_type).fg }}>
                              {goalAuthorBadge(g.created_by_type).text}
                            </span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c }}>{periodLabel}</span>
                            {archived && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: completed ? '#2fae4e' : '#e05252' }}>
                                {completed ? 'Tamamlandı' : 'Tamamlanmadı'}
                              </span>
                            )}
                            {!archived && completed && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2fae4e' }}>Tamamlandı</span>
                            )}
                          </div>
                          <p className="text-[14px] xl:text-[16px] font-bold" style={{ color: c }}>{subject}</p>
                          <p className="text-[12px] xl:text-[13px] font-semibold mt-0.5 text-gray-500">{goalPeriodRange(g)}</p>
                          <div className="flex items-center gap-2 mt-3">
                            <div className="flex-1 h-[8px] bg-[#e0e0e0] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: c }} />
                            </div>
                            <span className="text-[12px] xl:text-[14px] font-semibold" style={{ color: c }}>{achieved} / {target}</span>
                          </div>
                          {/* Auto goals: progress ticks up on its own from solved
                              questions. Legacy/teacher goals keep manual Veri Gir.
                              T9 — questions solved OUTSIDE Digipo can be logged
                              manually and are added on top of the auto count. */}
                          {g.auto_tracked ? (
                            <div className="mt-2">
                              <p className="text-[11px] text-gray-400 flex items-center gap-1 flex-wrap">
                                <span style={{ fontSize: 13 }}>⚡</span> bryekosis LMS'te çözdüğün sorularla otomatik güncellenir
                                {(g.manual_solved ?? 0) > 0 && (
                                  <span className="font-semibold text-gray-500">· Dışarıdan eklenen: {g.manual_solved} soru</span>
                                )}
                              </p>
                              {!archived && (!g.period_start || String(g.period_start).slice(0, 10) <= localTodayIso()) && (
                                <button
                                  onClick={() => {
                                    setEditingGoal(g);
                                    setFormError(null);
                                    const today = localTodayIso();
                                    const pe = g.period_end ? String(g.period_end).slice(0, 10) : '';
                                    setDisForm({ date: pe && today > pe ? pe : today, solved: '10', dogru: '0', yanlis: '0' });
                                    setModal('dis-veri');
                                  }}
                                  className="text-white text-[12px] xl:text-[13px] font-semibold px-4 py-1.5 rounded-[10px] mt-2"
                                  style={{ backgroundColor: c }}
                                >
                                  + Dışarıda Çözdüklerimi Ekle
                                </button>
                              )}
                            </div>
                          ) : !archived ? (
                            <button
                              onClick={() => {
                                setEditingGoal(g);
                                setFormData({ ...formData, toplamSoru: String(target), cozulenSoru: String(achieved), dogru: String(g.correct_count ?? 0), yanlis: String(g.wrong_count ?? 0) });
                                setModal('veri-gir');
                              }}
                              className="text-white text-[12px] xl:text-[14px] font-semibold px-5 py-1.5 rounded-[10px] mt-2"
                              style={{ backgroundColor: c }}
                            >
                              Veri Gir
                            </button>
                          ) : null}
                        </div>
                        <div className="flex-shrink-0 px-3 xl:px-5 py-3 flex flex-col justify-center gap-2 w-[120px] xl:w-[130px] border-l border-gray-200" style={{ borderLeftColor: c }}>
                          <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Doğru</span><span className="text-[15px] font-bold" style={{ color: c }}>{g.correct_count ?? 0}</span></div>
                          <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Yanlış</span><span className="text-[15px] font-bold" style={{ color: c }}>{g.wrong_count ?? 0}</span></div>
                          <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Başarı</span><span className="text-[15px] font-bold" style={{ color: c }}>%{g.accuracy_rate ?? 0}</span></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {modal === 'program-edit' && editingEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setModal(null); setEditingEntry(null); }} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[560px] p-6">
            <button onClick={() => { setModal(null); setEditingEntry(null); }} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <h2 className="text-[22px] font-semibold text-[#2b2b2b] mb-5">Program Düzenle</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-2">
                <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Konu</label>
                <input type="text" value={formData.goalTitle} onChange={(e) => setFormData({ ...formData, goalTitle: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Ders</label>
                <select value={formData.subjectUnitId} onChange={(e) => setFormData({ ...formData, subjectUnitId: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none">
                  <option value="">Genel</option>
                  {SUBJECT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Başlangıç</label>
                <input type="time" value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none" />
              </div>
              <div>
                <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Bitiş</label>
                <input type="time" value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Hedef Soru Sayısı</label>
                <input type="number" min="0" value={formData.targetCount} onChange={(e) => setFormData({ ...formData, targetCount: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none" />
              </div>
            </div>
            {formError && <p className="text-[13px] text-red-500 text-center mb-3">{formError}</p>}
            <button onClick={handleSaveEntryEdit} className="bg-[#3973bf] text-white text-[15px] font-bold px-10 py-3 rounded-[12px] hover:bg-blue-700 transition-colors block mx-auto">Kaydet</button>
          </div>
        </div>
      )}

      {modal === 'veri-gir' && editingGoal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[400px] p-6 text-center">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-xl">×</button>
            <p className="text-[15px] font-bold text-[#2b2b2b] mb-5">Çözülen Soru Sayısını Güncelle</p>
            <div className="mb-3">
              <label className="text-[12px] font-medium text-[#2b2b2b] mb-1 block">Çözülen Soru (Hedef: {editingGoal.target_value ?? 0})</label>
              <input type="number" min={0} value={formData.cozulenSoru} onChange={(e) => setFormData({ ...formData, cozulenSoru: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-center text-[16px] font-semibold outline-none" />
            </div>
            <div className="mb-5 flex gap-3">
              <div className="flex-1">
                <label className="text-[12px] font-medium text-[#3973bf] mb-1 block">Doğru</label>
                <input type="number" min={0} value={formData.dogru} onChange={(e) => setFormData({ ...formData, dogru: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-center text-[16px] font-semibold outline-none" />
              </div>
              <div className="flex-1">
                <label className="text-[12px] font-medium text-[#e5599b] mb-1 block">Yanlış</label>
                <input type="number" min={0} value={formData.yanlis} onChange={(e) => setFormData({ ...formData, yanlis: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#e5599b] text-center text-[16px] font-semibold outline-none" />
              </div>
            </div>
            {formError && <p className="text-[13px] text-red-500 text-center mb-3">{formError}</p>}
            <button onClick={handleUpdateGoal} className="bg-[#e5599b] text-white text-[15px] font-bold w-full py-3 rounded-[12px] hover:bg-pink-600 transition-colors">Kaydet</button>
          </div>
        </div>
      )}

      {/* T9 — manual entry for questions solved OUTSIDE Digipo. Adds a dated
          goal_practice_entries row; the goal card sums it on top of the
          auto-tracked platform count. */}
      {modal === 'dis-veri' && editingGoal && (() => {
        // Re-resolve from state so the entry list refreshes after save/delete
        // (editingGoal is a snapshot from before loadAll()).
        const disGoal = goals.find((x) => String(x.id) === String(editingGoal.id)) ?? editingGoal;
        const today = localTodayIso();
        const minDate = disGoal.period_start ? String(disGoal.period_start).slice(0, 10) : undefined;
        const peIso = disGoal.period_end ? String(disGoal.period_end).slice(0, 10) : undefined;
        const maxDate = peIso && peIso < today ? peIso : today;
        const entries = disGoal.goal_practice_entries ?? [];
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setModal(null); setEditingGoal(null); }} />
            <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[420px] p-6 max-h-[90vh] overflow-y-auto">
              <button onClick={() => { setModal(null); setEditingGoal(null); }} className="absolute top-4 right-4 text-[#2b2b2b] text-xl">×</button>
              <p className="text-[16px] font-bold text-[#2b2b2b] mb-1">Dışarıda Çözdüklerini Ekle</p>
              <p className="text-[12px] text-gray-500 mb-4">
                Kitaptan, dergiden ya da başka kaynaklardan çözdüğün sorular hedefine eklenir.
                <span className="block mt-0.5 font-semibold">
                  {(disGoal.subject_name || disGoal.goal_definitions?.subjects?.name || 'Genel')} · {goalPeriodRange(disGoal)}
                </span>
              </p>
              <div className="mb-3">
                <label className="text-[12px] font-medium text-[#2b2b2b] mb-1 block">Tarih</label>
                <input
                  type="date"
                  value={disForm.date}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => setDisForm({ ...disForm, date: e.target.value })}
                  className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-[14px] outline-none"
                />
              </div>
              <div className="mb-3">
                <label className="text-[12px] font-medium text-[#2b2b2b] mb-1 block">Çözülen Soru</label>
                <input type="number" min={1} value={disForm.solved} onChange={(e) => setDisForm({ ...disForm, solved: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-center text-[16px] font-semibold outline-none" />
              </div>
              <div className="mb-4 flex gap-3">
                <div className="flex-1">
                  <label className="text-[12px] font-medium text-[#3973bf] mb-1 block">Doğru</label>
                  <input type="number" min={0} value={disForm.dogru} onChange={(e) => setDisForm({ ...disForm, dogru: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#3973bf] text-center text-[16px] font-semibold outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-[12px] font-medium text-[#e5599b] mb-1 block">Yanlış</label>
                  <input type="number" min={0} value={disForm.yanlis} onChange={(e) => setDisForm({ ...disForm, yanlis: e.target.value })} className="w-full bg-white rounded-[12px] h-[44px] px-3 border border-[#e5599b] text-center text-[16px] font-semibold outline-none" />
                </div>
              </div>
              {formError && <p className="text-[13px] text-red-500 text-center mb-3">{formError}</p>}
              <button onClick={handleAddPractice} className="bg-[#e5599b] text-white text-[15px] font-bold w-full py-3 rounded-[12px] hover:bg-pink-600 transition-colors">Ekle</button>
              {entries.length > 0 && (
                <div className="mt-4">
                  <p className="text-[12px] font-semibold text-[#2b2b2b] mb-1.5">Eklediklerin</p>
                  <div className="space-y-1.5">
                    {entries.map((en) => (
                      <div key={String(en.id)} className="flex items-center gap-2 bg-white rounded-[10px] px-3 py-2">
                        <span className="text-[12px] text-gray-600 flex-1">
                          {(() => {
                            const d = new Date(en.entry_date);
                            return isNaN(d.getTime()) ? '—' : fmtUtcShortDate(d);
                          })()}
                          {' · '}{en.solved_questions} soru
                          <span className="text-gray-400"> (D:{en.correct_count} Y:{en.wrong_count})</span>
                        </span>
                        <button
                          onClick={() => handleDeletePractice(String(disGoal.id), String(en.id))}
                          aria-label="Kaydı sil"
                          className="w-6 h-6 rounded-full hover:bg-red-100 flex items-center justify-center text-[14px] text-gray-500 hover:text-red-600 leading-none"
                        >×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {modal === 'yeni-hedef' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[440px] p-6">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <h2 className="text-[20px] font-semibold text-[#2b2b2b] mb-1">Yeni Hedef</h2>
            <p className="text-[13px] text-gray-500 mb-5">Çözdüğün sorular hedefe otomatik olarak sayılır.</p>

            <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Hedef Süresi</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {(['daily', 'weekly', 'monthly'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setGoalForm({ ...goalForm, periodType: p })}
                  className={`py-2.5 rounded-[12px] text-[14px] font-semibold transition-colors ${goalForm.periodType === p ? 'bg-[#3973bf] text-white' : 'bg-white text-[#2b2b2b] border border-gray-200'}`}
                >{PERIOD_LABEL[p]}</button>
              ))}
            </div>

            {/* T8 — pick WHICH day/week/month the goal is for. Deliberately
                a single type="date" input for all three periods: date inputs
                open a native calendar in EVERY browser (Chrome, Edge,
                Firefox, Safari, iOS). Do NOT switch this to type="week" or
                type="month" — Safari and Firefox never implemented those and
                would silently degrade to a bare text box. The resolved
                window is previewed right below, so haftalık/aylık stay
                unambiguous even though the student picks a single day. */}
            <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">
              {goalForm.periodType === 'daily' ? 'Hangi Gün?' : goalForm.periodType === 'weekly' ? 'Hangi Hafta? (o haftadan bir gün seç)' : 'Hangi Ay? (o aydan bir gün seç)'}
            </label>
            <input
              type="date"
              value={goalForm.date}
              onChange={(e) => setGoalForm({ ...goalForm, date: e.target.value })}
              className="w-full bg-white rounded-[12px] h-[46px] px-3 border border-[#3973bf] text-[14px] outline-none"
            />
            {(() => {
              const p = resolveGoalPeriod(goalForm.periodType, goalForm.date);
              if (!p) return <p className="text-[12px] text-red-400 mt-1 mb-4">Geçerli bir tarih seçin.</p>;
              const label = p.start.getTime() === p.end.getTime()
                ? fmtUtcShortDate(p.start)
                : `${fmtUtcShortDate(p.start)} – ${fmtUtcShortDate(p.end)}`;
              return <p className="text-[12px] text-gray-500 mt-1 mb-4">Hedef dönemi: <span className="font-semibold">{label}</span></p>;
            })()}

            <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Ders</label>
            <select
              value={goalForm.subjectId}
              onChange={(e) => setGoalForm({ ...goalForm, subjectId: e.target.value })}
              className="w-full bg-white rounded-[12px] h-[46px] px-3 border border-[#3973bf] text-[14px] outline-none mb-4"
            >
              <option value="">Genel (Tüm Dersler)</option>
              {SUBJECT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label className="text-[13px] font-medium text-[#3973bf] mb-1.5 block">Hedef Soru Sayısı</label>
            <input
              type="number" min={1}
              value={goalForm.targetCount}
              onChange={(e) => setGoalForm({ ...goalForm, targetCount: e.target.value })}
              className="w-full bg-white rounded-[12px] h-[46px] px-3 border border-[#3973bf] text-[16px] font-semibold outline-none mb-5"
            />

            {formError && <p className="text-[13px] text-red-500 text-center mb-3">{formError}</p>}
            <button onClick={handleCreateGoal} className="bg-[#e5599b] text-white text-[15px] font-bold w-full py-3 rounded-[12px] hover:bg-pink-600 transition-colors">Hedefi Oluştur</button>
          </div>
        </div>
      )}

      {/* B17 — haftalık hedef tamamlandı kutlaması */}
      {showWeekWin && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowWeekWin(false)} />
          <div className="relative bg-white rounded-[24px] w-full max-w-[400px] px-6 py-7 text-center shadow-2xl">
            <div className="flex items-end justify-center gap-2 mb-3">
              <FlameIcon size={26} color="#fb923c" />
              <FlameIcon size={40} color="#f97316" />
              <FlameIcon size={26} color="#fb923c" />
            </div>
            <img src="/a-digikoc-mascot.png" alt="Digikoç" className="w-[128px] h-[128px] object-contain mx-auto" />
            <p className="text-[22px] font-bold text-[#e5599b] mt-1">Tebrikler!</p>
            <p className="text-[14px] text-[#2b2b2b] mt-1.5 leading-snug">
              Bu haftaki hedeflerini tamamladın. Alevin sönmesin, böyle devam!
            </p>
            <button
              onClick={() => setShowWeekWin(false)}
              className="mt-5 bg-[#e5599b] text-white text-[15px] font-bold px-8 py-2.5 rounded-[14px] hover:bg-pink-600"
            >Harika!</button>
          </div>
        </div>
      )}

      {modal === 'confirm-delete' && confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setModal(null); setConfirmTarget(null); }} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[400px] p-6 text-center">
            <p className="text-[16px] font-semibold text-[#2b2b2b] mb-2">Silmek istediğinize emin misiniz?</p>
            <p className="text-[13px] text-gray-500 mb-6">
              {confirmTarget.kind === 'goal' ? 'Bu hedef silinecek.' : 'Bu program silinecek.'} Bu işlem geri alınamaz.
            </p>
            {formError && <p className="text-[13px] text-red-500 mb-3">{formError}</p>}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setModal(null); setConfirmTarget(null); setFormError(null); }}
                className="bg-gray-100 text-[#2b2b2b] text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-gray-200"
              >Vazgeç</button>
              <button
                onClick={executeConfirmedDelete}
                className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-pink-600"
              >Sil</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'kaydedildi' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-20" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[16px] px-10 py-6 text-center">
            <p className="text-[18px] font-bold text-[#3973bf]">Başarıyla Kaydedildi</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentSchedule;
